import React, { useCallback, useMemo, useState, useRef, RefObject, memo } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    IconButton,
    Stack,
    Typography,
    Paper,
    Alert,
    AlertTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import { PaperProps } from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Controller, useForm } from 'react-hook-form';
import type { Control, FieldErrors, UseFormGetValues } from 'react-hook-form';
import Draggable from 'react-draggable';
import type { AccrualProfile } from 'repository/timekeeper/@types/AccrualProfile';

import { useAccrualProfileData } from './hooks/useAccrualProfileData';
import { useAccrualProfileRows } from './hooks/useAccrualProfileRows';
import { useAccrualProfileForm, INDEFINITE_DATE } from './hooks/useAccrualProfileForm';
import type { AccrualProfileRow, FormValues } from 'repository/timekeeper/@types/AccrualProfile';

// ─────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────

type Props = {
    open: boolean;
    setOpen: (v: boolean) => void;
    data: Employee;
    cleanSelected: () => void;
    reloading: () => void;
};

type Employee = {
    id: number;
    [key: string]: any;
};

// ─────────────────────────────────────────────────────────────
// Cell component shared props
// ─────────────────────────────────────────────────────────────

type CellSharedProps = {
    rowIndex: number;
    control: Control<FormValues>;
    errors: FieldErrors<FormValues>;
    loading: boolean;
    getValues: UseFormGetValues<FormValues>;
    validateRow: (row: AccrualProfileRow, index: number) => string | null;
    t: TFunction;
};

type ProfileSelectCellProps = CellSharedProps & { profiles: AccrualProfile[] };

const ProfileSelectCell = memo<ProfileSelectCellProps>(({ rowIndex, control, loading, profiles, validateRow, getValues, t }) => (
    <Controller
        name={`profiles.${rowIndex}.accrual_profile_id`}
        control={control}
        rules={{
            validate: (_, formValues) => {
                return validateRow(formValues.profiles[rowIndex], rowIndex) ?? true;
            },
        }}
        render={({ field, fieldState }) => {
            // getValues es síncrono y no produce re-renders (seguro en render)
            const validationError = validateRow(getValues(`profiles.${rowIndex}`), rowIndex);
            return (
                <FormControl fullWidth size="small" error={!!fieldState.error || !!validationError}>
                    <InputLabel id={`ap-label-${rowIndex}`}>{t('timekeeper.employees.dialog.assign_accrual_profile.profile_label')}</InputLabel>
                    <Select {...field} labelId={`ap-label-${rowIndex}`} label={t('timekeeper.employees.dialog.assign_accrual_profile.profile_label')} disabled={loading}>
                        {profiles.map((profile) => (
                            <MenuItem key={profile.accrual_profile_id} value={profile.accrual_profile_id}>
                                {profile.name || profile.description || `Perfil ${profile.accrual_profile_id}`}
                            </MenuItem>
                        ))}
                    </Select>
                    {validationError && <FormHelperText>{validationError}</FormHelperText>}
                </FormControl>
            );
        }}
    />
));

type DateCellProps = CellSharedProps & { fieldName: 'start_date' | 'end_date' };

const DateCell = memo<DateCellProps>(({ rowIndex, fieldName, control, loading, validateRow, getValues, t }) => {
    const isEndDate = fieldName === 'end_date';
    const labelKey = isEndDate ? 'timekeeper.employees.dialog.assign_accrual_profile.end_date_label' : 'timekeeper.employees.dialog.assign_accrual_profile.start_date_label';
    const requiredKey = isEndDate ? 'timekeeper.employees.dialog.assign_accrual_profile.end_date_required' : 'timekeeper.employees.dialog.assign_accrual_profile.start_date_required';

    return (
        <Controller
            name={`profiles.${rowIndex}.${fieldName}`}
            control={control}
            rules={{
                required: t(requiredKey),
                validate: (_, formValues) => {
                    return validateRow(formValues.profiles[rowIndex], rowIndex) ?? true;
                },
            }}
            render={({ field, fieldState }) => {
                const validationError = validateRow(getValues(`profiles.${rowIndex}`), rowIndex);
                return (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <DatePicker
                            value={field.value}
                            onChange={field.onChange}
                            inputRef={field.ref}
                            sx={{ mt: 0.5 }}
                            label={t(labelKey)}
                            {...(isEndDate && {
                                localeText: {
                                    okButtonLabel: t('timekeeper.employees.dialog.assign_accrual_profile.set_indefinite'),
                                },
                            })}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    error: !!fieldState.error || !!validationError,
                                    helperText: fieldState.error?.message || validationError || '',
                                },
                                ...(isEndDate && {
                                    actionBar: {
                                        actions: ['accept' as const],
                                        onAccept: () => field.onChange(INDEFINITE_DATE),
                                    },
                                }),
                            }}
                        />
                    </LocalizationProvider>
                );
            }}
        />
    );
});

// ─────────────────────────────────────────────────────────────
// Draggable Paper
// ─────────────────────────────────────────────────────────────

const PaperComponent = (props: PaperProps) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable nodeRef={nodeRef as RefObject<HTMLDivElement>} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
};

// ─────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────

export const AssignAccrualProfile: React.FC<Props> = ({ open, setOpen, data, cleanSelected, reloading }) => {
    const { t } = useTranslation();
    const employeeId = data?.id;

    const [alerts, setAlerts] = useState<boolean>(false);

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        getValues,
    } = useForm<FormValues>({
        defaultValues: { profiles: [] },
        mode: 'onChange',
    });

    const closeForm = useCallback(() => {
        reset({ profiles: [] });
        setAlerts(false);
        cleanSelected();
        setOpen(false);
    }, [reset, cleanSelected, setOpen]);

    const handleClose = useCallback(() => {
        if (isDirty) setAlerts(true);
        else closeForm();
    }, [isDirty, closeForm]);

    // ── Hook: gestión de filas ────────────────────────────────

    const { rows, addRow, removeRow, initRows } = useAccrualProfileRows({
        employeeId,
        control,
        reset,
    });

    // ── Hook: fetching ────────────────────────────────────────
    //
    // onLoaded conecta el resultado del fetch con initRows.
    // useAccrualProfileData desconoce cómo se almacenan las filas.

    const { availableProfiles, loading } = useAccrualProfileData({
        employeeId,
        open,
        onLoaded: initRows,
    });

    // ── Hook: validación + submit ─────────────────────────────

    const { validateRow, onSubmit } = useAccrualProfileForm({
        employeeId,
        availableProfiles,
        getValues,
        onSuccess: reloading,
        onClose: closeForm,
    });

    // ── Columnas ─────────────────────────────────────────────

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'actions',
                type: 'actions',
                headerName: 'Acciones',
                flex: 0.5,
                headerAlign: 'center',
                align: 'center',
                getActions: (params: any) => {
                    const rowIndex: number = params.row._index;
                    const isLastRow = rowIndex === rows.length - 1;
                    const isFirstRow = rowIndex === 0;

                    const actions: React.ReactElement[] = [];

                    if (!(isFirstRow && isLastRow)) {
                        actions.push(
                            <GridActionsCellItem
                                key="delete"
                                icon={<DeleteIcon color="error" />}
                                label={t('general.buttons.delete')}
                                onClick={() => removeRow(rowIndex)}
                                disabled={loading}
                                showInMenu={false}
                            />,
                        );
                    }

                    if (isLastRow) {
                        actions.push(<GridActionsCellItem key="add" icon={<AddIcon color="action" />} label={t('general.buttons.add')} onClick={addRow} showInMenu={false} />);
                    }

                    return actions;
                },
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'accrual_profile_id',
                headerName: t('timekeeper.employees.dialog.assign_accrual_profile.profile_label'),
                flex: 1.5,
                align: 'center',
                renderCell: (params: any) => (
                    <ProfileSelectCell
                        rowIndex={params.row._index}
                        control={control}
                        errors={errors}
                        loading={loading}
                        profiles={availableProfiles}
                        validateRow={validateRow}
                        getValues={getValues}
                        t={t}
                    />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'start_date',
                headerName: t('timekeeper.employees.dialog.assign_accrual_profile.start_date_label'),
                flex: 1,
                align: 'center',
                renderCell: (params: any) => (
                    <DateCell rowIndex={params.row._index} fieldName="start_date" control={control} errors={errors} loading={loading} validateRow={validateRow} getValues={getValues} t={t} />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'end_date',
                headerName: t('timekeeper.employees.dialog.assign_accrual_profile.end_date_label'),
                flex: 1,
                align: 'center',
                renderCell: (params: any) => (
                    <DateCell rowIndex={params.row._index} fieldName="end_date" control={control} errors={errors} loading={loading} validateRow={validateRow} getValues={getValues} t={t} />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, availableProfiles, rows.length, errors, loading, control, addRow, removeRow, validateRow, getValues],
    );

    // ── Render ────────────────────────────────────────────────

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" PaperComponent={PaperComponent}>
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('timekeeper.employees.dialog.assign_accrual_profile.title')}
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            {alerts && (
                <Box sx={{ top: 0, zIndex: 10, pb: 1, px: 3, pt: 2 }}>
                    <Alert
                        variant="filled"
                        severity="warning"
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>
                                <Button onClick={closeForm} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                </Box>
            )}

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body1" gutterBottom>
                        {data?.full_name}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        {t('timekeeper.employees.dialog.assign_accrual_profile.assignments_profiles')}
                    </Typography>

                    {loading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        // FIX #8: <form> eliminado; era HTML inválido dentro de DataGrid divs.
                        // El submit se maneja vía handleSubmit en el botón de DialogActions.
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            density="standard"
                            disableColumnMenu
                            disableRowSelectionOnClick
                            hideFooter
                            hideFooterPagination
                            hideFooterSelectedRowCount
                            showCellVerticalBorder
                            showColumnVerticalBorder
                            rowHeight={80}
                            autosizeOptions={{ outliersFactor: 0.5 }}
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontSize: '0.875rem',
                                    fontWeight: 'bold',
                                    whiteSpace: 'normal',
                                    lineHeight: 1.2,
                                    textAlign: 'center',
                                    height: 'auto',
                                },
                                '& .MuiDataGrid-row': {
                                    transition: 'all 0.3s ease-in-out',
                                },
                            }}
                        />
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={handleClose}>
                    {t('general.buttons.close')}
                </Button>
                <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={loading}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
